import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import TicTacToeBoard from './TicTacToeBoard';
import {
  createInitialGameState,
  isValidMove,
  makeMove,
  getGameStatus,
  getNextTurn,
} from './logic';
import {
  subscribeToGame,
  updateGameState,
  updateGameStatus,
  requestRematch,
  resetGameForRematch,
  saveGameToHistory,
  updateMatchStats,
  deleteGame,
  checkBothRematchRequested,
} from '../../services/gameService';
import { addRecentOpponent } from '../../services/friendService';
import { leaveRoom } from '../../services/roomService';
import { database } from '../../services/firebase';
import { ref, update, serverTimestamp } from 'firebase/database';
import { safeString } from '../../utils/safeString';
import './TicTacToe.css';

const TicTacToe = ({ roomCode, roomData, user }) => {
  console.log('TicTacToe rendering:', { roomCode, roomData, user });

  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [opponentRematchRequested, setOpponentRematchRequested] = useState(false);
  const [error, setError] = useState(null);
  const confettiShownRef = useRef(false);
  const gameRecordedRef = useRef(false);

  // Validate props
  if (!roomCode || !roomData || !user) {
    console.error('Missing required props:', { roomCode, roomData, user });
    return (
      <div className="tictactoe-container">
        <div className="error-message">Missing required data. Please try again.</div>
      </div>
    );
  }

  const isHost = roomData.host && roomData.host.userId === user.uid;
  const opponent = isHost ? roomData.guest : roomData.host;

  // If opponent left, show waiting state
  if (!opponent) {
    const handleReturnToLobby = async () => {
      try {
        const roomRef = ref(database, `rooms/${roomCode}`);
        await update(roomRef, {
          status: 'waiting',
          gameSelected: null,
          lastActivity: serverTimestamp()
        });
        navigate(`/room/${roomCode}`);
      } catch (error) {
        console.error('Error returning to lobby:', error);
        navigate(`/room/${roomCode}`);
      }
    };

    return (
      <div className="tictactoe-container">
        <div className="game-header">
          <h2>Tic Tac Toe</h2>
        </div>
        <div className="player-waiting-state">
          <div className="waiting-spinner"></div>
          <h3>Player left the room</h3>
          <p>Waiting for player to return...</p>
          <p className="waiting-hint">The game will resume when they come back</p>
          <button className="leave-button" onClick={handleReturnToLobby}>
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  // Get player symbols from game state
  const mySymbol = gameState ? (isHost ? gameState.hostSymbol : gameState.guestSymbol) : null;
  const opponentSymbol = mySymbol === 'X' ? 'O' : 'X';

  console.log('Player info:', { isHost, opponent, mySymbol, opponentSymbol });

  // Subscribe to game updates
  useEffect(() => {
    console.log('Setting up game subscription for room:', roomCode);

    try {
      const unsubscribe = subscribeToGame(roomCode, async (game) => {
        console.log('========================================');
        console.log('Game update received:', game);
        console.log('Game type:', typeof game);
        console.log('Game keys:', game ? Object.keys(game) : 'null');

        if (game) {
          console.log('game.gameState:', game.gameState);
          console.log('gameState type:', typeof game.gameState);
          console.log('gameState keys:', game.gameState ? Object.keys(game.gameState) : 'undefined');

          if (game.gameState) {
            console.log('gameState.board:', game.gameState.board);
            console.log('board type:', typeof game.gameState.board);
            console.log('board is array:', Array.isArray(game.gameState.board));
          }
        }
        console.log('========================================');

        if (game && game.gameState) {
          console.log('Setting gameState to:', game.gameState);
          setGameState(game.gameState);

          // Check rematch requests
          if (game.rematchRequests) {
            const myRequest = !!game.rematchRequests[user.uid];
            const opponentRequest = !!game.rematchRequests[opponent.userId];

            setRematchRequested(myRequest);
            setOpponentRematchRequested(opponentRequest);

            // If both players requested rematch, only the host resets the game
            // This prevents race conditions where both players try to reset
            if (myRequest && opponentRequest && isHost && game.gameState.isGameOver) {
              console.log('Both players requested rematch, host is resetting game...');

              const newGameState = createInitialGameState(
                roomData.host.userId,
                roomData.guest.userId
              );

              await resetGameForRematch(
                roomCode,
                'tictactoe',
                newGameState,
                {
                  host: roomData.host,
                  guest: roomData.guest,
                }
              );
            }
          } else {
            setRematchRequested(false);
            setOpponentRematchRequested(false);
          }
        } else if (game === null) {
          console.warn('Game not found in database');
          setError('Game not found. It may not have been created yet.');
        } else if (game && !game.gameState) {
          console.error('Game exists but has no gameState property');
          setError('Game data is missing gameState. Please try restarting the game.');
        }

        setLoading(false);
      });

      return () => {
        console.log('Cleaning up game subscription');
        unsubscribe();
      };
    } catch (err) {
      console.error('Error setting up game subscription:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [roomCode, user.uid, opponent.userId, isHost, roomData]);

  // Trigger confetti on win (only once per game)
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameState.isDraw && !confettiShownRef.current) {
      const iWon = gameState.winner === mySymbol;

      if (iWon) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        confettiShownRef.current = true;
      }
    }

    // Reset the flags when game restarts (not game over anymore)
    if (gameState && !gameState.isGameOver) {
      confettiShownRef.current = false;
      gameRecordedRef.current = false;
    }
  }, [gameState, mySymbol]);

  // Save game to history when it ends (only once)
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameRecordedRef.current) {
      gameRecordedRef.current = true;

      const iWon = gameState.winner === mySymbol;
      const result = gameState.isDraw ? 'draw' : iWon ? 'won' : 'lost';

      // Convert winner symbol to user ID (if not a draw)
      const winnerUserId = gameState.isDraw
        ? 'draw'
        : (gameState.winner === mySymbol ? user.uid : opponent.userId);

      // Save to game history
      saveGameToHistory(user.uid, {
        gameType: 'tictactoe',
        opponent: safeString(opponent?.displayName, 'Opponent'),
        opponentId: opponent.userId,
        result,
        winner: winnerUserId,
        isDraw: gameState.isDraw,
        roomCode,
      });

      // Update match stats
      updateMatchStats(user.uid, 'tictactoe', result);

      // Add to recent opponents
      addRecentOpponent(user.uid, {
        userId: opponent.userId,
        displayName: safeString(opponent?.displayName, 'Opponent'),
        photoURL: safeString(opponent?.photoURL, ''),
        lastGame: 'tictactoe',
        lastResult: result,
      });

      // Update game status
      updateGameStatus(roomCode, 'completed');
    }
  }, [gameState, mySymbol, user.uid, opponent, roomCode]);

  const handleCellClick = async (index) => {
    if (!gameState || gameState.isGameOver) return;

    // Check if it's my turn
    if (gameState.currentTurn !== mySymbol) {
      return;
    }

    // Validate move
    if (!isValidMove(gameState.board, index)) {
      return;
    }

    // Make move
    const newBoard = makeMove(gameState.board, index, mySymbol);

    // Check game status
    const status = getGameStatus(newBoard);

    // Update game state
    const newGameState = {
      ...gameState,
      board: newBoard,
      currentTurn: getNextTurn(gameState.currentTurn),
      moveCount: gameState.moveCount + 1,
      isGameOver: status.isOver,
      winner: status.winner,
      isDraw: status.isDraw,
      winningCombination: status.winningCombination,
    };

    // Update Firebase
    await updateGameState(roomCode, newGameState);
  };

  const handleRematch = async () => {
    // Just request the rematch - the subscription will handle the reset
    await requestRematch(roomCode, user.uid);
    // Local state will be updated by the subscription
  };

  const handleLeaveGame = async () => {
    try {
      // Update room status back to waiting and clear game selections
      const roomRef = ref(database, `rooms/${roomCode}`);
      await update(roomRef, {
        status: 'waiting',
        gameSelected: null,
        'host/gameSelection': null,
        'guest/gameSelection': null,
        lastActivity: serverTimestamp()
      });
      // Navigate back to room lobby
      navigate(`/room/${roomCode}`);
    } catch (error) {
      console.error('Error leaving game:', error);
      // Navigate to room lobby anyway
      navigate(`/room/${roomCode}`);
    }
  };

  if (loading) {
    return (
      <div className="tictactoe-container">
        <div className="loading-message">
          <div className="spinner-large"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tictactoe-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="tictactoe-container">
        <div className="error-message">
          <p>Game not found or not yet created</p>
          <p>Please wait a moment or return to lobby</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  // Additional validation for gameState
  if (!gameState.board || !Array.isArray(gameState.board)) {
    console.error('Invalid gameState - missing or invalid board:', gameState);
    return (
      <div className="tictactoe-container">
        <div className="error-message">
          <p>Invalid game state</p>
          <p>The game data is corrupted or incomplete</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  const isMyTurn = gameState.currentTurn === mySymbol;
  const iWon = gameState.isGameOver && gameState.winner === mySymbol;
  const iLost = gameState.isGameOver && gameState.winner === opponentSymbol;

  return (
    <div className="tictactoe-container">
      {/* Header */}
      <div className="game-header">
        <h1 className="game-title">⭕ Tic Tac Toe ✖️</h1>
      </div>

      {/* Players Info */}
      <div className="players-info">
        <div className={`player-card ${isMyTurn && !gameState.isGameOver ? 'active' : ''}`} data-symbol={mySymbol}>
          <img src={safeString(user?.photoURL, '')} alt={safeString(user?.displayName, 'You')} className="player-avatar" />
          <div className="player-details">
            <p className="player-name">You ({mySymbol})</p>
          </div>
        </div>

        <div className="vs-divider">VS</div>

        <div className={`player-card ${!isMyTurn && !gameState.isGameOver ? 'active' : ''}`} data-symbol={opponentSymbol}>
          <img src={safeString(opponent?.photoURL, '')} alt={safeString(opponent?.displayName, 'Opponent')} className="player-avatar" />
          <div className="player-details">
            <p className="player-name">{safeString(opponent?.displayName, 'Opponent')} ({opponentSymbol})</p>
          </div>
        </div>
      </div>

      {/* Turn Indicator */}
      {!gameState.isGameOver && (
        <div className="turn-indicator-section">
          <p className="turn-indicator">
            {isMyTurn ? "Your Turn!" : `${safeString(opponent?.displayName, 'Opponent')}'s Turn`}
          </p>
        </div>
      )}

      {/* Game Status */}
      {!gameState.isGameOver && (
        <div className="game-status">
          <p className="status-text">
            {isMyTurn ? "It's your turn!" : `Waiting for ${safeString(opponent?.displayName, 'Opponent')}...`}
          </p>
        </div>
      )}

      {/* Game Board */}
      <TicTacToeBoard
        board={gameState.board}
        onCellClick={handleCellClick}
        winningCombination={gameState.winningCombination}
        disabled={!isMyTurn || gameState.isGameOver}
      />

      {/* Game Over */}
      {gameState.isGameOver && (
        <div className="game-over">
          <div className={`result-message ${iWon ? 'won' : iLost ? 'lost' : 'draw'}`}>
            {gameState.isDraw ? (
              <>
                <span className="result-icon">🤝</span>
                <h2>It's a Draw!</h2>
                <p>Great game! Want to play again?</p>
              </>
            ) : iWon ? (
              <>
                <span className="result-icon">🎉</span>
                <h2>You Won!</h2>
                <p>Congratulations on your victory!</p>
              </>
            ) : (
              <>
                <span className="result-icon">😞</span>
                <h2>You Lost</h2>
                <p>Better luck next time!</p>
              </>
            )}
          </div>

          {/* Rematch Section */}
          <div className="rematch-section">
            {!rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button" onClick={handleRematch}>
                🔄 Request Rematch
              </button>
            )}

            {rematchRequested && !opponentRematchRequested && (
              <div className="rematch-status">
                <p>⏳ Waiting for {safeString(opponent?.displayName, 'Opponent')} to accept rematch...</p>
              </div>
            )}

            {!rematchRequested && opponentRematchRequested && (
              <div className="rematch-status">
                <p>🎮 {safeString(opponent?.displayName, 'Opponent')} wants a rematch!</p>
                <button className="rematch-button accept" onClick={handleRematch}>
                  ✓ Accept Rematch
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Move Counter */}
      <div className="move-counter">
        <p>Moves: {gameState.moveCount}</p>
      </div>

      {/* Leave Game Button - Always Visible */}
      <div className="leave-game-section">
        <button className="leave-button" onClick={handleLeaveGame}>
          Leave Game
        </button>
      </div>
    </div>
  );
};

export default TicTacToe;
